/**
 * useAvailableContexts.ts
 *
 * Hook for fetching contexts available for a sphere,
 * based on approaches deployed to the sphere and its ancestors, plus system contexts,
 * filtered by each context's applicability rules.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';
import type { Context } from 'types/context';

/**
 * The server-resolved default context for the sphere + viewer.
 * Resolution (most specific wins): current arc stage's perspective rule →
 * arc stage default → schema perspective rule → schema default_context.
 * Only contexts available on the sphere can win; null means "no authored
 * default" and clients fall back to their component default.
 */
export interface ResolvedDefaultContext {
  contextSystemName: string;
  source: 'surface_binding' | 'arc_stage_perspective' | 'arc_stage' | 'schema_perspective' | 'schema';
}

/** Data returned by GET /spheres/:sphereId/available-contexts */
export interface AvailableContextsData {
  contexts: Context[];
  approachSources: Record<string, string[]>;
  defaultContext?: ResolvedDefaultContext | null;
}

/**
 * Process-wide cache of resolved available-contexts, keyed by sphere id and scoped
 * to the `ApiClients` that produced it.
 *
 * This hook is instantiated independently by five components (the context
 * selector popover, the context switcher toolbar, the armillary view, the
 * context shell and the header), several of which mount together on a single
 * page. Without a shared cache each one issued its own
 * `GET /spheres/:id/available-contexts` for the same sphere, so the request
 * count scaled with how many consumers a given view happened to mount rather
 * than with the data actually needed.
 *
 * Entries hold the in-flight promise AND, once it settles, the resolved payload.
 * The promise collapses consumers that mount in the same tick, before any response
 * has landed; the resolved payload lets a consumer mounting slightly later seed its
 * initial state synchronously instead of flashing a loading state.
 *
 * Scoping by client matters because two `ApiClients` can be live at once (a re-auth
 * or an impersonation swap hands out a new one) and need not speak for the same
 * identity — available contexts are viewer-dependent, so one client must never be
 * served the other's answer.
 */
export const AVAILABLE_CONTEXTS_MEMO_TTL_MS = 5000;

interface MemoEntry {
  promise: Promise<AvailableContextsData>;
  /** Set once `promise` resolves, so `peek` can serve it without awaiting. */
  value?: AvailableContextsData;
  expiresAt: number;
}

let memoByClient = new WeakMap<ApiClients, Map<string, MemoEntry>>();

function memoFor(apiClients: ApiClients): Map<string, MemoEntry> {
  let memo = memoByClient.get(apiClients);
  if (!memo) {
    memo = new Map();
    memoByClient.set(apiClients, memo);
  }
  return memo;
}

/**
 * Drop the shared payload for one sphere (every sphere when omitted), for one client
 * (every client when omitted). Call after a mutation that can change which contexts a
 * sphere exposes — deploying or removing an approach, or editing applicability rules.
 */
export function invalidateAvailableContexts(apiClients?: ApiClients, sphereId?: string): void {
  if (!apiClients) {
    // No client named: the caller cannot enumerate the WeakMap, so hand out a fresh
    // one. Every existing memo becomes unreachable and is collected with its client.
    memoByClient = new WeakMap<ApiClients, Map<string, MemoEntry>>();
    return;
  }
  const memo = memoByClient.get(apiClients);
  if (!memo) return;
  if (sphereId) memo.delete(sphereId);
  else memo.clear();
}

/** Synchronously read a still-fresh, already-resolved cache entry, or undefined. */
function peekAvailableContexts(
  apiClients: ApiClients | null,
  sphereId: string | null | undefined
): AvailableContextsData | undefined {
  if (!apiClients || !sphereId) return undefined;
  const hit = memoByClient.get(apiClients)?.get(sphereId);
  return hit && hit.expiresAt > Date.now() ? hit.value : undefined;
}

function readAvailableContexts(
  apiClients: ApiClients,
  sphereId: string,
  skipCache: boolean
): Promise<AvailableContextsData> {
  const memo = memoFor(apiClients);
  const now = Date.now();
  if (!skipCache) {
    const hit = memo.get(sphereId);
    if (hit && hit.expiresAt > now) return hit.promise;
  }

  const params: Record<string, string> = {};
  if (skipCache) {
    params.skipCache = 'true';
  }

  const promise = apiClients.graphClient
    .get<AvailableContextsData>(`/spheres/${sphereId}/available-contexts`, { params })
    .then((data) => {
      const current = memo.get(sphereId);
      if (current?.promise === promise) current.value = data;
      return data;
    })
    .catch((err: unknown) => {
      // A failure is not a result to share: the next caller retries.
      if (memo.get(sphereId)?.promise === promise) memo.delete(sphereId);
      throw err;
    });

  memo.set(sphereId, { promise, expiresAt: now + AVAILABLE_CONTEXTS_MEMO_TTL_MS });
  return promise;
}

/** Return value of the useAvailableContexts hook */
export interface UseAvailableContextsReturn {
  contexts: Context[];
  approachSources: Record<string, string[]>;
  defaultContext: ResolvedDefaultContext | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches available contexts for a sphere based on deployed approaches.
 * Instances mounted within AVAILABLE_CONTEXTS_MEMO_TTL_MS of each other share
 * one request; `refetch` always bypasses and replaces the shared payload.
 *
 * @param apiClients - API clients (pass null if not ready)
 * @param sphereId - Sphere to fetch available contexts for (null/undefined skips fetch)
 */
export function useAvailableContexts(
  apiClients: ApiClients | null,
  sphereId: string | null | undefined
): UseAvailableContextsReturn {
  // Seed from the shared cache so a consumer mounting after another has already
  // resolved this sphere renders populated on its first paint, instead of flashing
  // an empty/loading state while a redundant round-trip it no longer makes settles.
  const seed = peekAvailableContexts(apiClients, sphereId);
  const [contexts, setContexts] = useState<Context[]>(seed?.contexts ?? []);
  const [approachSources, setApproachSources] = useState<Record<string, string[]>>(
    seed?.approachSources ?? {}
  );
  const [defaultContext, setDefaultContext] = useState<ResolvedDefaultContext | null>(
    seed?.defaultContext ?? null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const refetchRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchAvailableContexts = useCallback(
    async (skipCache = false) => {
      if (!apiClients?.graphClient || !sphereId) return;

      // A cache hit resolves without a network round-trip, so raising `loading`
      // would only produce a spurious spinner frame.
      const servedFromCache = !skipCache && !!peekAvailableContexts(apiClients, sphereId);
      if (!servedFromCache) {
        setLoading(true);
      }
      setError(null);

      try {
        const data = await readAvailableContexts(apiClients, sphereId, skipCache);

        if (isMountedRef.current) {
          setContexts(data.contexts);
          setApproachSources(data.approachSources);
          setDefaultContext(data.defaultContext ?? null);
        }
      } catch (err: unknown) {
        if (isMountedRef.current) {
          setError(
            err instanceof Error ? err.message : 'Failed to fetch available contexts'
          );
        }
      } finally {
        if (!servedFromCache && isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [apiClients, sphereId]
  );

  // Fetch on mount and when sphereId changes
  useEffect(() => {
    if (!apiClients || !sphereId) {
      setContexts([]);
      setApproachSources({});
      setDefaultContext(null);
      return;
    }
    fetchAvailableContexts();
  }, [apiClients, sphereId, refetchRef.current, fetchAvailableContexts]);

  const refetch = useCallback(() => {
    refetchRef.current += 1;
    fetchAvailableContexts(true);
  }, [fetchAvailableContexts]);

  return { contexts, approachSources, defaultContext, loading, error, refetch };
}
